// Onda 2 (2026-06-02): migrado pra Antd — Table + Alert + Upload + Modal.confirm + Tag.
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Table, Button, Tag, Space, Alert, Empty, Spin, Typography, App as AntApp,
  Checkbox, Modal, Popconfirm, Upload, Input, Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PaperClipOutlined, ThunderboltOutlined, DownloadOutlined,
  DeleteOutlined, RollbackOutlined, SettingOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import type { Negocio } from './types';
import { fmtDate } from './utils';
import { ModalIniciarContrato } from './ModalIniciarContrato';
import type { OpcoesKickoff } from './ModalIniciarContrato';
import { usePerms } from '../../hooks/usePerms';

export function AbaVendas({ negocioId, negocio, onAbrirDoc, reloadFlag, onNegocioChanged, autoDesmarcarTick }: {
  negocioId: number; negocio: Negocio;
  onAbrirDoc: (id: string) => void; reloadFlag: number;
  onNegocioChanged?: () => void;
  // incrementado pelo botão "Desmarcar Ganho" do topo do negócio → abre o modal aqui
  autoDesmarcarTick?: number;
}) {
  const { message } = AntApp.useApp();
  // Desmarcar o ganho e anexar o contrato do cliente a negócio ainda não ganho
  // (que o marca ganho) pedem a régua de quem gera a venda (06/10/2026). No
  // gêmeo da licitação nenhum dos dois: a etapa acompanha a licitação.
  const { pode, erro: permsErro } = usePerms();
  const podeVenda = permsErro || pode('comercial.negocio_privado.gerar_venda');
  const ehGemeo = (negocio as any)?.origem === 'licitacao';
  const podeDesmarcar = podeVenda && !ehGemeo;
  const podeAnexarContrato = negocio?.status === 'ganho' || (podeVenda && !ehGemeo);
  const [docs, setDocs] = useState<any[]>([]);
  const [contratoExistente, setContratoExistente] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingDesmarcar, setPendingDesmarcar] = useState(0);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [enviandoContrato, setEnviandoContrato] = useState(false);
  const [iniciandoContrato, setIniciandoContrato] = useState(false);
  const [modalIniciarAberto, setModalIniciarAberto] = useState(false);
  const inputContratoRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { carregar(); }, [negocioId, reloadFlag, mostrarHistorico]);

  // Botão "Desmarcar Ganho" do topo do negócio aciona o modal aqui (após os
  // docs carregarem, pra a opção de excluir doc de venda aparecer correta).
  useEffect(() => { if (autoDesmarcarTick) setPendingDesmarcar(autoDesmarcarTick); }, [autoDesmarcarTick]);
  useEffect(() => {
    if (pendingDesmarcar && !loading && negocio?.status === 'ganho' && podeDesmarcar) {
      setPendingDesmarcar(0);
      desmarcarGanho();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDesmarcar, loading]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ proposta_id: String(negocioId), tipo: 'venda' });
      if (!mostrarHistorico) params.set('vigente', 'true');
      const [resDocs, resContratos] = await Promise.all([
        axios.get(`${API_URL}/comercial/propostas-geradas?${params}`,
          { headers: { Authorization: token } }),
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_listar_contratos' },
          { headers: { Authorization: token } }).catch(() => null),
      ]);
      const data = resDocs.data?.body ? JSON.parse(resDocs.data.body) : resDocs.data;
      setDocs(Array.isArray(data) ? data : []);
      if (resContratos) {
        const cs = resContratos.data?.body ? JSON.parse(resContratos.data.body) : resContratos.data;
        const meu = (Array.isArray(cs) ? cs : []).find((c: any) => c.negocio_id === negocioId);
        setContratoExistente(meu || null);
      }
    } finally { setLoading(false); }
  }

  // Desmarcar ganho — pergunta se exclui docs venda + motivo + novo status.
  // Bloqueia se ja ha contrato operacional vinculado (regra backend).
  function desmarcarGanho() {
    const docsAtivos = (docs || []).filter(d =>
      (d.tipo === 'venda' || d.tipo === 'contrato_externo') &&
      d.status !== 'cancelada' && d.status !== 'descartada'
    );

    let motivoState = '';
    let novoStatusState = '';
    let excluirDocsState = false;

    Modal.confirm({
      title: 'Desmarcar ganho do negócio',
      width: 520,
      content: (
        <div style={{ marginTop: 12 }}>
          {docsAtivos.length > 0 && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={`${docsAtivos.length} documento(s) de venda neste negócio`}
              description={
                <Checkbox onChange={(e) => { excluirDocsState = e.target.checked; }}>
                  Excluir também os documentos de venda
                </Checkbox>
              } />
          )}
          <Typography.Text style={{ fontSize: 12, color: '#666' }}>
            Motivo da reversão (opcional):
          </Typography.Text>
          <Input placeholder="ex: cliente mudou de ideia"
            onChange={(e) => { motivoState = e.target.value; }} />
          <Typography.Text style={{ fontSize: 12, color: '#666', marginTop: 10, display: 'block' }}>
            Volta para a etapa:
          </Typography.Text>
          <Select style={{ width: '100%' }} allowClear
            placeholder="Etapa anterior (automático)"
            onChange={(v) => { novoStatusState = v || ''; }}
            options={[
              { value: 'prospeccao', label: 'Prospecção' },
              { value: 'qualificacao', label: 'Qualificação' },
              { value: 'proposta', label: 'Proposta' },
              { value: 'follow_up', label: 'Follow-up' },
              { value: 'negociacao', label: 'Negociação' },
            ]} />
        </div>
      ),
      okText: 'Reverter',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
            {
              action: 'com_desmarcar_ganho',
              negocio_id: negocioId,
              excluir_doc_venda: excluirDocsState,
              motivo: motivoState || null,
              novo_status: novoStatusState.trim() || undefined,
            },
            { headers: { Authorization: token } });
          const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
          const docsExc = d?.docs_excluidos?.length
            ? ` Doc(s) venda excluído(s): ${d.docs_excluidos.join(', ')}.` : '';
          message.success(`Negócio voltou para "${d.novo_status}".${docsExc}`);
          await carregar();
          // L18b (2026-06-03): notifica parent pra recarregar neg.status
          // (sem isso, o botão "Ganhar" continua achando que está em ganho
          // e mostra o confirm "já está marcado como Ganho")
          onNegocioChanged?.();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  function iniciarContrato() {
    if (negocio.status !== 'ganho') {
      message.warning('Só é possível iniciar contrato para negócio com status "Ganho". Use ✓ Ganhar no topo do negócio antes.');
      return;
    }
    setModalIniciarAberto(true);
  }

  async function executarIniciarContrato(dataAssinatura: string, opts: OpcoesKickoff = {}) {
    setIniciandoContrato(true);
    try {
      const token = await getToken();
      // Modo "vincular a existente": só amarra a venda como origem de um contrato
      // já vigente (sem negócio); não cria contrato, serviços nem parcelas.
      if (opts.vincular_origem_contrato_id) {
        const rv = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_vincular_negocio_origem', negocio_id: negocioId, contrato_id: opts.vincular_origem_contrato_id },
          { headers: { Authorization: token } });
        const dv = rv.data?.body ? JSON.parse(rv.data.body) : rv.data;
        message.success(`Venda vinculada ao contrato ${dv?.numero || ''} como origem.`);
        setModalIniciarAberto(false);
        await carregar();
        if (onNegocioChanged) onNegocioChanged();
        return;
      }
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        {
          action: 'op_iniciar_contrato',
          negocio_id: negocioId,
          data_assinatura_contrato: dataAssinatura,
          unificar: true,
          // 2026-06-22: se vier, vincula a venda a um contrato existente (aditivo).
          contrato_pai_id: opts.contrato_pai_id || undefined,
          motivo_aditivo: opts.motivo_aditivo || undefined,
          // KICKOFF (2026-08-20): equipe e prazos vêm do wizard. O backend exige
          // gestor e coordenador e recusa parcela sem prazo em dias.
          gestor_email: opts.gestor_email || undefined,
          coordenador_email: opts.coordenador_email || undefined,
          equipe_emails: opts.equipe_emails || undefined,
          parcelas_prazos: opts.parcelas_prazos || undefined,
          // 05/10/2026: a área escolhida no kick-off para os itens que estavam
          // sem. O backend grava na venda e recusa o que continuar sem área.
          itens_areas: opts.itens_areas?.length ? opts.itens_areas : undefined,
        },
        { headers: { Authorization: token } });
      const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
      const heranca = data?.heranca || {};
      const avisados = (data?.kickoff?.avisados || []).length;
      message.success({
        content: `Contrato ${data?.contrato?.numero} criado! ${heranca.entregaveis || 0} serviço(s), `
          + `${heranca.parcelas || 0} parcela(s)`
          + (avisados ? `, ${avisados} pessoa(s) avisada(s) por e-mail.` : '.'),
        duration: 4,
      });
      // O backend não derruba a criação por falha de e-mail nem de vínculo, mas
      // avisa — e quem criou precisa saber (senão o gestor toma 403 depois).
      for (const aviso of (data?.avisos || [])) message.warning({ content: aviso, duration: 8 });
      setModalIniciarAberto(false);

      const contratoId = data?.contrato?.id;
      if (contratoId) {
        try {
          localStorage.setItem('app:modulo', 'operacional');
          localStorage.setItem('app:pagina', 'op-contratos');
          localStorage.setItem('op:abrir_contrato_id', contratoId);
        } catch {}
        window.location.reload();
        return;
      }
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setIniciandoContrato(false); }
  }

  async function excluir(id: string, titulo: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/comercial/propostas-geradas/${id}`,
        { headers: { Authorization: token } });
      message.success(`"${titulo}" excluído`);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function anexarContratoExterno(file: File) {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      message.warning('Apenas arquivos PDF são aceitos como contrato.'); return;
    }
    if (file.size > 30 * 1024 * 1024) {
      message.warning('Arquivo acima de 30MB. Comprima ou divida.'); return;
    }
    setEnviandoContrato(true);
    try {
      const token = await getToken();
      const r1 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'anexos_upload_url', negocio_id: negocioId,
          filename: file.name, content_type: 'application/pdf' },
        { headers: { Authorization: token } });
      const data1 = r1.data?.body ? JSON.parse(r1.data.body) : r1.data;
      await axios.put(data1.upload_url, file, {
        headers: { 'Content-Type': 'application/pdf' },
        timeout: 180000,
      });
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'anexar_contrato_externo',
          negocio_id: negocioId, s3_key: data1.s3_key,
          nome: file.name, tamanho_bytes: file.size },
        { headers: { Authorization: token } });
      await carregar();
      message.success('Contrato anexado e venda formalizada (negócio marcado como Ganho).');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally {
      setEnviandoContrato(false);
      if (inputContratoRef.current) inputContratoRef.current.value = '';
    }
  }

  async function baixarContratoExterno(id: string) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'baixar_contrato_externo', id },
        { headers: { Authorization: token } });
      const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
      window.open(data.download_url, '_blank');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  const columns: ColumnsType<any> = [
    { title: 'Número', dataIndex: 'numero_proposta', key: 'numero', width: 110,
      render: (v) => v ? <span style={{ fontFamily: 'monospace', color: '#020D7F' }}>{v}</span> : '—' },
    { title: 'Título', dataIndex: 'titulo', key: 'titulo',
      ellipsis: { showTitle: false },
      render: (v, d) => {
        const conteudo = d.tipo === 'contrato_externo' ? `📎 ${v}` : v;
        const onClick = () => d.tipo === 'contrato_externo'
          ? baixarContratoExterno(d.id) : onAbrirDoc(d.id);
        return (
          <a onClick={onClick} title={v}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}>
            {conteudo}
          </a>
        );
      } },
    { title: 'Tipo', key: 'tipo', width: 130, align: 'center',
      render: (_, d) => d.tipo === 'contrato_externo'
        ? <Tag color="gold" bordered={false}>Contrato cliente</Tag>
        : <Tag color="blue" bordered={false}>Doc. interno</Tag> },
    { title: 'Versão', key: 'versao', width: 90, align: 'center',
      render: (_, d) => <span style={{ fontSize: 11 }}>v{d.versao}{d.revisao > 0 ? ` · R${d.revisao}` : ''}</span> },
    { title: 'Atualizado', dataIndex: 'atualizado_em', key: 'atualizado', width: 110, align: 'center',
      render: (v) => <Typography.Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(v)}</Typography.Text> },
    { title: 'Ações', key: 'acoes', width: 100, align: 'center',
      render: (_, d) => (
        <Space size={4}>
          {d.tipo === 'contrato_externo' && (
            <Button size="small" icon={<DownloadOutlined />}
              title="Baixar contrato" onClick={() => baixarContratoExterno(d.id)} />
          )}
          <Popconfirm title={`Excluir "${d.titulo}"?`} okText="Excluir" okType="danger" cancelText="Cancelar"
            onConfirm={() => excluir(d.id, d.titulo)}>
            <Button size="small" danger icon={<DeleteOutlined />} title="Excluir definitivamente" />
          </Popconfirm>
        </Space>
      ) },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space size={14} wrap>
          <Typography.Text strong style={{ color: '#020D7F' }}>
            {docs.length} documento{docs.length === 1 ? '' : 's'} de venda
          </Typography.Text>
          <Checkbox checked={mostrarHistorico}
            onChange={e => setMostrarHistorico(e.target.checked)}>
            <span style={{ fontSize: 12 }}>Mostrar histórico de revisões</span>
          </Checkbox>
        </Space>
        {podeAnexarContrato && (
          <Upload showUploadList={false} accept=".pdf,application/pdf"
            beforeUpload={(file) => { anexarContratoExterno(file as File); return false; }}>
            <Button icon={<PaperClipOutlined />} loading={enviandoContrato}>
              {enviandoContrato ? 'Enviando…' : 'Anexar contrato externo'}
            </Button>
          </Upload>
        )}
      </Space>

      <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 12 }}
        message="Dois caminhos pra formalizar a venda"
        description={
          <span style={{ fontSize: 12 }}>
            <strong>(1) Documento interno:</strong> clique em <strong>✓ Ganhar</strong> no topo do negócio
            pra gerar nosso doc de venda (com assinatura via D4 Sign).<br />
            <strong>(2) Contrato do cliente:</strong> clique em <strong>Anexar contrato externo</strong>
            acima pra subir o PDF que o cliente envia (clientes maiores costumam exigir).<br />
            Ambos satisfazem o requisito do CREA pra gerar a CAT depois.
          </span>
        }
      />

      {/* Iniciar contrato (Operacional) — so se status=ganho */}
      {negocio.status === 'ganho' && (
        <Alert
          type={contratoExistente ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            contratoExistente
              ? <span><strong>Contrato {contratoExistente.numero}</strong> ativo no módulo Operacional — status: <strong>{contratoExistente.status}</strong>.</span>
              : <strong>Negócio fechado.</strong>
          }
          description={
            contratoExistente
              ? 'Acompanhe execução, atividades e drive em Operacional → Contratos.'
              : 'Próximo passo: iniciar contrato no módulo Operacional. Anexos, mapa, proposta e venda serão herdados.'
          }
          action={!contratoExistente && (
            <Space>
              <Button type="primary" icon={<ThunderboltOutlined />}
                onClick={iniciarContrato} loading={iniciandoContrato}
                style={{ background: '#B07500', borderColor: '#B07500' }}>
                Iniciar contrato
              </Button>
              {podeDesmarcar && (
                <Button icon={<RollbackOutlined />} onClick={desmarcarGanho}
                  title="Reverter status de ganho. Útil pra ajustes ou cancelamento antes de iniciar contrato.">
                  Desmarcar ganho
                </Button>
              )}
            </Space>
          )}
        />
      )}
      {modalIniciarAberto && (
        <ModalIniciarContrato
          negocioId={negocioId}
          clienteId={negocio.cliente_id}
          loading={iniciandoContrato}
          onClose={() => setModalIniciarAberto(false)}
          onConfirmar={executarIniciarContrato}
        />
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : docs.length === 0 ? (
        <Empty description="Nenhum documento de venda registrado neste negócio" />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={docs} pagination={false}
          size="small" tableLayout="fixed" />
      )}
    </div>
  );
}
